// src/pages/WHSCommandCentre.tsx — G-06: WHS Command Centre
// Cross-module safety KPI dashboard, lag/lead indicators, AI agent integration point,
// and government audit export hub. Default landing for Safety & Env nav group.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_WHS_INSPECTIONS } from '../utils/mockDataWHS'
import { MOCK_JOBS } from '../utils/mockData'
import { formatCurrency } from '../utils/formatters'
import { MOCK_CARS, CAR_STATUS_META, MOCK_COMPLIANCE_REGISTER, getWHSDocumentStyle } from '../utils/mockDataWHSMgmt'
import { AlertTriangle, CheckCircle, Clock, Shield, Zap, TrendingDown, TrendingUp, FileText, Download, Sparkles, Activity, BarChart3, X } from 'lucide-react'

const fmtDate = (s?: string | null) => s ? new Date(s).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'2-digit' }) : '—'

// ── WHS frequency rate calculator ──────────────────────────────
function calcLTIFR(ltiCount: number, hoursWorked: number) { return hoursWorked > 0 ? ((ltiCount / hoursWorked) * 1_000_000).toFixed(2) : '0.00' }
function calcTRIFR(triCount: number, hoursWorked: number)  { return hoursWorked > 0 ? ((triCount  / hoursWorked) * 1_000_000).toFixed(2) : '0.00' }

// ── KPI card ────────────────────────────────────────────────────
function KPICard({ label, value, sub, color, border, trend, icon }: { label: string; value: string | number; sub: string; color: string; border: string; trend?: 'up'|'down'|'flat'; icon: React.ReactNode }) {
  return (
    <div className={`border rounded-xl p-4 ${border}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
          <div className="text-xs font-medium text-gray-800 mt-0.5">{label}</div>
          <div className="text-xs text-gray-500">{sub}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className={`${color} opacity-60`}>{icon}</div>
          {trend && <div className={`text-[10px] font-medium ${trend==='up'?'text-red-600':trend==='down'?'text-green-600':'text-gray-400'}`}>{trend==='up'?'↑ Increasing':trend==='down'?'↓ Decreasing':'→ Stable'}</div>}
        </div>
      </div>
    </div>
  )
}

// ── AI Agent integration panel ──────────────────────────────────
function AIAgentPanel() {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="bg-gradient-to-r from-[#1F3864] to-[#2E75B6] rounded-xl p-4 text-white">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold text-sm flex items-center gap-2">WHS AI Agent <span className="text-[10px] bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full font-bold">Planned</span></div>
            <div className="text-xs text-blue-200 mt-0.5">The WHS AI Agent will proactively analyse platform data to surface compliance risks before they become incidents. Connects to site drawings, SWMS, roster, and inspection data.</div>
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-blue-300 hover:text-white text-xs underline flex-shrink-0">{expanded ? 'Less' : 'Capabilities'}</button>
      </div>
      {expanded && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-blue-100">
          {['Analyse site drawings → auto-identify hazards pre-mobilisation', 'Predict compliance events 14 days in advance', 'Auto-draft SWMS hazard register from drawing data', 'Flag fatigue-risk roster patterns before they occur', 'Generate government audit summary reports from live data', 'Cross-reference SWMS controls against active incident patterns'].map(c => (
            <div key={c} className="flex items-start gap-1.5 bg-white/10 rounded-lg px-2.5 py-1.5"><Sparkles className="w-2.5 h-2.5 flex-shrink-0 mt-0.5 text-amber-300"/>{c}</div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Audit export panel ──────────────────────────────────────────
function AuditExportPanel({ style }: { style: ReturnType<typeof getWHSDocumentStyle> }) {
  const [exported, setExported] = useState<string[]>([])
  const exports = [
    { id: 'whs-summary',    label: 'WHS Monthly Summary',        desc: 'Incidents, inspections, CARs, KPIs',  icon: '📊' },
    { id: 'car-report',     label: 'Corrective Action Register',  desc: 'All CARs with status and evidence',   icon: '📋' },
    { id: 'compliance-rpt', label: 'Legislative Compliance Report',desc: 'PCBU duties compliance status',       icon: '⚖️' },
    { id: 'incident-log',   label: 'Incident Register',           desc: 'Full incident log with investigation', icon: '⚠️' },
    { id: 'training-matrix',label: 'Training & Competency Matrix', desc: 'VOC, licences, inductions per worker',icon: '🎓' },
    { id: 'audit-pack',     label: 'WorkSafe Audit Package',      desc: 'All key WHS docs for inspector',      icon: '🏛️' },
  ]
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2">
        <FileText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
        All exported documents use the MCB Civil Electrical branding and format settings configured in Settings. Documents are formatted to Australian Standards for WHS record keeping.
      </div>
      <div className="grid grid-cols-2 gap-2">
        {exports.map(e => (
          <div key={e.id} className={`flex items-center justify-between rounded-xl border px-3 py-2.5 ${exported.includes(e.id) ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
            <div className="flex items-center gap-2.5">
              <span className="text-base">{e.icon}</span>
              <div><div className="text-xs font-semibold text-gray-900">{e.label}</div><div className="text-[10px] text-gray-400">{e.desc}</div></div>
            </div>
            <button onClick={() => setExported(p => [...p, e.id])} className={`text-xs px-2 py-1 rounded-lg font-medium flex items-center gap-1 flex-shrink-0 ${exported.includes(e.id) ? 'text-green-700 bg-green-100' : 'text-[#2E75B6] bg-blue-50 hover:bg-blue-100 border border-blue-200'}`}>
              {exported.includes(e.id) ? <><CheckCircle className="w-3 h-3" />Done</> : <><Download className="w-3 h-3" />Export</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────
export function WHSCommandCentre() {
  const { isTier } = useAuthStore()
  const isPM = isTier('B_PM') || isTier('A_DEVELOPER')
  const [tab, setTab] = useState<'overview'|'kpis'|'audit'>('overview')
  const style = getWHSDocumentStyle()

  // Simulated period data
  const HOURS_WORKED     = 4_280  // YTD hours across all jobs
  const LTI_COUNT        = 0      // Lost time injuries YTD
  const TRI_COUNT        = 1      // Total recordable injuries YTD (medical treatment)
  const NEAR_MISS_YTD    = 5
  const HAZARD_OBS_YTD   = 12

  const openCARs  = MOCK_CARS.filter(c => c.status === 'OPEN' || c.status === 'IN_PROGRESS').length
  const overdueCARs = MOCK_CARS.filter(c => c.status === 'OVERDUE').length
  const openIssues = MOCK_WHS_INSPECTIONS.flatMap(i => i.issues ?? []).filter((i: any) => i.status !== 'CLOSED').length
  const complianceIssues = MOCK_COMPLIANCE_REGISTER.filter(c => c.status === 'NON_COMPLIANT' || c.status === 'PARTIAL').length
  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).length

  const LTIFR = calcLTIFR(LTI_COUNT, HOURS_WORKED)
  const TRIFR = calcTRIFR(TRI_COUNT, HOURS_WORKED)

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Shield className="w-6 h-6 text-[#2E75B6]" />WHS Command Centre</h1>
          <p className="text-sm text-gray-500 mt-0.5">Safety performance · compliance status · corrective actions · audit export</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">YTD period — Jan 2026 to date</span>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">{activeJobs} active jobs</span>
        </div>
      </div>

      {/* Critical alerts */}
      {overdueCARs > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span><strong>{overdueCARs} corrective action{overdueCARs!==1?'s':''} overdue.</strong> Overdue CARs represent a legislative non-compliance risk. Review and close immediately or escalate to PM.</span>
        </div>
      )}
      {complianceIssues > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
          <Shield className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span><strong>{complianceIssues} legislative compliance item{complianceIssues!==1?'s':''}</strong> require attention — see Compliance Register.</span>
        </div>
      )}

      {/* Coverage mapping alert — links WHSO to the coverage mapping page */}
      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Shield className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-800">
            <strong>8 planned activities</strong> across 4 active jobs have no approved SWMS — work must not commence.
          </span>
        </div>
        <button
          onClick={() => (document.dispatchEvent(new CustomEvent('tp-navigate', { detail: 'whs-coverage' })))}
          className="text-xs text-red-700 bg-red-100 hover:bg-red-200 border border-red-300 px-3 py-1.5 rounded-lg font-medium whitespace-nowrap flex-shrink-0">
          View coverage map →
        </button>
      </div>

      <AIAgentPanel />

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {(['overview','kpis','audit'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 text-sm font-medium px-4 py-2 rounded-lg transition-colors capitalize ${tab===t?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t==='overview'?'📊 Overview':t==='kpis'?'📈 Safety KPIs':'🏛️ Audit Export'}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <KPICard label="LTIFR" value={LTIFR} sub="Lost time injury freq. rate (per 1M hrs)" color={parseFloat(LTIFR)>0?'text-red-600':'text-green-700'} border={parseFloat(LTIFR)>0?'border-red-200 bg-red-50':'border-green-200 bg-green-50'} trend="flat" icon={<Activity className="w-4 h-4"/>}/>
            <KPICard label="TRIFR" value={TRIFR} sub="Total recordable injury freq. rate" color={parseFloat(TRIFR)>5?'text-red-600':'text-amber-600'} border={parseFloat(TRIFR)>5?'border-red-200 bg-red-50':'border-amber-200 bg-amber-50'} trend="down" icon={<TrendingDown className="w-4 h-4"/>}/>
            <KPICard label="Hours worked YTD" value={HOURS_WORKED.toLocaleString()} sub="All active jobs combined" color="text-gray-900" border="border-gray-200 bg-white" icon={<Clock className="w-4 h-4"/>}/>
            <KPICard label="Near misses / hazard obs YTD" value={NEAR_MISS_YTD + HAZARD_OBS_YTD} sub={`${NEAR_MISS_YTD} near miss · ${HAZARD_OBS_YTD} hazard obs`} color={NEAR_MISS_YTD+HAZARD_OBS_YTD>10?'text-amber-600':'text-green-700'} border="border-gray-200 bg-white" trend="down" icon={<Zap className="w-4 h-4"/>}/>
            <KPICard label="Open corrective actions" value={openCARs} sub={`${overdueCARs} overdue`} color={overdueCARs>0?'text-red-600':openCARs>0?'text-amber-600':'text-green-700'} border={overdueCARs>0?'border-red-200 bg-red-50':'border-gray-200 bg-white'} icon={<AlertTriangle className="w-4 h-4"/>}/>
            <KPICard label="Open inspection issues" value={openIssues} sub="Unresolved site issues" color={openIssues>5?'text-red-600':openIssues>0?'text-amber-600':'text-green-700'} border={openIssues>0?'border-amber-200 bg-amber-50':'border-gray-200 bg-white'} icon={<CheckCircle className="w-4 h-4"/>}/>
          </div>

          {/* Lead/lag indicator table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
              <div><div className="text-sm font-semibold text-gray-900">Lead and lag indicator summary</div><div className="text-xs text-gray-500 mt-0.5">ISO 45001 cl.9.1 — safety performance measurement</div></div>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50 border-b text-gray-500 uppercase text-[10px] tracking-wide font-semibold"><th className="text-left px-4 py-2.5">Indicator</th><th className="text-left px-3 py-2.5">Type</th><th className="text-right px-3 py-2.5">Value</th><th className="text-left px-3 py-2.5">Target</th><th className="text-center px-3 py-2.5">Status</th></tr></thead>
              <tbody className="divide-y divide-gray-50">
                {[
                  { name:'LTIFR', type:'Lag', value:`${LTIFR}`, target:'0.00', ok: parseFloat(LTIFR)===0 },
                  { name:'TRIFR', type:'Lag', value:`${TRIFR}`, target:'<5.00', ok: parseFloat(TRIFR)<5 },
                  { name:'Near miss / hazard observation rate', type:'Lead', value:`${NEAR_MISS_YTD+HAZARD_OBS_YTD} YTD`, target:'>10/yr (higher = better reporting culture)', ok: true },
                  { name:'Pre-start inspection completion rate', type:'Lead', value:'87%', target:'>95%', ok: false },
                  { name:'Toolbox talk frequency', type:'Lead', value:'Weekly', target:'Weekly minimum', ok: true },
                  { name:'CAR close-out rate (within due date)', type:'Lead', value:`${MOCK_CARS.filter(c=>c.status==='VERIFIED').length}/${MOCK_CARS.length}`, target:'>90%', ok: false },
                  { name:'WHS training currency', type:'Lead', value:'78% current', target:'>95%', ok: false },
                  { name:'Subcontractor safety audit completion', type:'Lead', value:'Monthly', target:'Monthly', ok: true },
                ].map(r => (
                  <tr key={r.name} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-900">{r.name}</td>
                    <td className="px-3 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${r.type==='Lag'?'bg-orange-100 text-orange-700':'bg-blue-100 text-blue-700'}`}>{r.type}</span></td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{r.value}</td>
                    <td className="px-3 py-2.5 text-gray-500">{r.target}</td>
                    <td className="px-3 py-2.5 text-center">{r.ok ? <span className="text-green-600">✅</span> : <span className="text-red-600">❌</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'kpis' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Incident trend by month */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#2E75B6]"/>Incidents YTD by month</div>
              {[['Jan','0'],['Feb','0'],['Mar','1'],['Apr','0'],['May','0'],['Jun','1'],['Jul','0']].map(([m,v]) => (
                <div key={m} className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 w-6">{m}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                    <div className={`h-full rounded ${parseInt(v)>0?'bg-orange-400':'bg-green-300'}`} style={{width: `${parseInt(v)*50+5}%`}}/>
                  </div>
                  <span className="text-xs font-semibold w-3">{v}</span>
                </div>
              ))}
            </div>
            {/* CAR status breakdown */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">Corrective actions by status</div>
              {Object.entries(CAR_STATUS_META).map(([s, m]) => {
                const count = MOCK_CARS.filter(c => c.status === s).length
                return count > 0 ? (
                  <div key={s} className="flex items-center gap-2 mb-1.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${m.bg} ${m.text} w-24 text-center`}>{m.label}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                      <div className={`h-full rounded ${m.dot}`} style={{width:`${(count/MOCK_CARS.length)*100}%`}}/>
                    </div>
                    <span className="text-xs font-semibold w-3">{count}</span>
                  </div>
                ) : null
              })}
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-600"/>
            When the WHS AI Agent is available, it will automatically calculate predictive metrics including: estimated time to next incident based on leading indicator trends, risk-weighted activity score per job, and automated monthly WHS report generation.
          </div>
        </div>
      )}

      {tab === 'audit' && <AuditExportPanel style={style}/>}
    </div>
  )
}
